import { useState, useMemo, useEffect, type ElementType } from 'react';
import {
  Flex,
  Box,
  Input,
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  InputRightAddon,
  Icon,
  Tooltip,
  type InputProps,
} from '@chakra-ui/react';
import { KText } from '../../text';

// TODO: Change Icons once kula-icons new version published
import { InfoCircle, XClose } from '@kula-ai/icons-core';

interface KInputProps extends Omit<InputProps, 'onChange'> {
  label?: string;
  isRequired?: boolean;
  labelProps?: any;
  leftIcon?: ElementType;
  rightIcon?: ElementType;
  maxLength?: number;
  error?: any;
  info?: string;
  additionalInfo?: string;
  isDisabled?: boolean;
  clear?: boolean;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: any;
  props?: any;
  endAddon?: React.ReactNode;
  CustomRightElement?: any;
  beforeMaxLength?: boolean;
  customMaxLengthProps?: any;
  customRightElementProps?: any;
  endAddonWrapperProps?: React.ComponentProps<typeof InputRightAddon>;
  CustomLeftElement?: any;
  customLeftElementProps?: any;
  AfterInputGroupContent?: React.ComponentType<any>;
  // When true, render AfterInputGroupContent beside the input (flex row)
  // instead of stacked below it.
  inlineAfterInputGroupContent?: boolean;
  fieldType?: string;
  min?: number;
  inputHeight?: string;
  inputStyle?: Record<string, any>;
}

/**
 * A customizable input component that supports labels, icons, validation, and more
 * @param {KInputProps} props - The component props
 * @returns {JSX.Element} The rendered input component
 */
export const KInput: React.FC<KInputProps> = ({
  id,
  label,
  isRequired = false,
  labelProps,
  leftIcon,
  rightIcon,
  maxLength,
  error,
  info,
  additionalInfo,
  isDisabled = false,
  endAddon,
  endAddonWrapperProps = {},
  clear,
  CustomRightElement,
  beforeMaxLength = true,
  onChange = (_event) => {},
  value,
  customRightElementProps,
  CustomLeftElement,
  customLeftElementProps,
  AfterInputGroupContent,
  inlineAfterInputGroupContent = false,
  inputStyle,
  ...props
}) => {
  const {
    customMaxLengthProps = {},
    fieldType,
    min = 0,
    style: containerStyle,
    inputHeight = '42px',
  } = props;
  const [charCount, setCharCount] = useState(String(value)?.length);
  const [customValue, setCustomValue] = useState(value);
  useEffect(() => {
    setCustomValue(value);
  }, [value]);

  const hasError = Boolean(error);
  const inlineAfterContent =
    inlineAfterInputGroupContent && !!AfterInputGroupContent;

  const getElementColor = useMemo(() => {
    if (hasError) return 'error-200';
    if (isDisabled) return 'secondary-300';
    return undefined;
  }, [hasError, isDisabled]);

  const handleOnChange = (event) => {
    const newValue = event.target.value;

    // For number inputs, manually enforce maxLength since HTML doesn't support it natively
    if (
      fieldType === 'number' &&
      maxLength &&
      String(newValue).length > maxLength
    ) {
      return; // Don't update if exceeds maxLength
    }

    setCustomValue(newValue);
    if (maxLength) setCharCount(String(newValue).length);
    onChange(event);
  };

  const handleClear = () => {
    setCustomValue('');
    setCharCount(0);
    const event = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  };

  const calculateRightPadding = useMemo(() => {
    let padding = 12;
    if (rightIcon) padding += 26;
    if (clear && customValue) padding += 26;
    if (maxLength) {
      const counterWidth = String(maxLength).length * 8 + 30;
      padding += counterWidth;
    }
    return `${padding}px`;
  }, [rightIcon, clear, customValue, maxLength]);

  const calculateLeftPadding = useMemo(() => {
    let padding = 12;
    if (CustomLeftElement) padding += 90;
    if (leftIcon) padding += 40;
    return `${padding}px`;
  }, [CustomLeftElement, leftIcon]);

  return (
    <>
      <FormControl
        isRequired={isRequired}
        isInvalid={hasError}
        isDisabled={isDisabled}
        {...(inlineAfterContent ? { flex: '1', minW: 0 } : {})}
      >
        {label && (
          <Flex id={id} gap="4px" alignItems="center" mb="6px">
            <Flex gap="4px">
              <KText
                variant="heading_4"
                color={getElementColor}
                {...labelProps}
              >
                {label}
              </KText>
              {isRequired && <KText color={'error-200'}>*</KText>}
            </Flex>
            {info && (
              <Tooltip
                label={info}
                arrowSize={8}
                hasArrow={true}
                placement="top"
                variant="default"
              >
                <Box>
                  <Icon
                    // @ts-expect-error - InfoCircle is an IconType
                    as={InfoCircle}
                    w="16px"
                    h="16px"
                    cursor="pointer"
                    color={getElementColor}
                  />
                </Box>
              </Tooltip>
            )}
          </Flex>
        )}
        <InputGroup
          w="100%"
          minH="44px"
          alignItems={endAddon ? 'stretch' : 'center'}
          transition="background-color 0.15s ease, border-color 0.15s ease"
          bg={
            hasError
              ? 'pastel-1-light'
              : isDisabled
                ? 'secondary-25'
                : 'base-white'
          }
          _hover={{
            borderColor: hasError ? 'error-200' : 'secondary-300',
            bg: hasError
              ? 'pastel-1-light'
              : isDisabled
                ? 'secondary-25'
                : 'secondary-25',
            boxShadow: 'none',
          }}
          _focusWithin={{
            borderColor: hasError ? 'error-200' : 'secondary-300',
            bg: hasError ? 'pastel-1-light' : 'secondary-25',
            boxShadow: 'none',
          }}
          border="1px solid"
          borderColor={hasError ? 'error-200' : 'secondary-100'}
          outlineOffset="0px !important"
          borderRadius="6px"
          {...(containerStyle as object)}
        >
          {CustomLeftElement && (
            <InputLeftElement
              pointerEvents="none"
              mt="2px"
              left="0px"
              {...customLeftElementProps}
            >
              <CustomLeftElement />
            </InputLeftElement>
          )}
          {leftIcon && (
            <InputLeftElement pointerEvents="none" mt="2px">
              <Icon
                // @ts-expect-error - leftIcon is an IconType
                as={leftIcon}
                w="18px"
                h="18px"
                color={getElementColor}
              />
            </InputLeftElement>
          )}
          <Input
            h={inputHeight}
            flex="1"
            fontSize="14px"
            alignSelf={endAddon ? 'center' : undefined}
            {...props}
            value={customValue}
            maxLength={maxLength}
            min={min}
            onChange={handleOnChange}
            type={fieldType}
            border="none"
            outline="none"
            boxShadow="none"
            bg="transparent"
            color={hasError ? 'error-200' : 'secondary-700'}
            sx={{
              paddingRight: calculateRightPadding,
              paddingLeft: calculateLeftPadding,
              ...(props.style || {}),
              ...(inputStyle || {}),
            }}
            _invalid={{
              border: 'none',
              boxShadow: 'none',
            }}
            _placeholder={{
              opacity: 1,
              fontSize: '14px',
              fontWeight: 'medium',
              color: hasError ? 'error-100' : 'secondary-500',
            }}
            _hover={{
              border: 'none !important',
              boxShadow: 'none !important',
              outline: 'none !important',
            }}
            _focus={{
              border: 'none !important',
              boxShadow: 'none !important',
              outline: 'none !important',
              bg: 'transparent',
            }}
            onWheel={(e) => {
              if (fieldType === 'number') {
                e.currentTarget.blur();
              }
            }}
          />
          {endAddon && (
            <InputRightAddon
              h="44px"
              border="none"
              bg="transparent"
              pl="8px"
              pr="12px"
              {...endAddonWrapperProps}
            >
              {endAddon}
            </InputRightAddon>
          )}

          {CustomRightElement && beforeMaxLength && (
            <InputRightElement
              mt="2px"
              w="fit-content"
              right="60px"
              {...customRightElementProps}
            >
              <CustomRightElement showError={hasError} />
            </InputRightElement>
          )}

          {(rightIcon || clear || maxLength) && (
            <InputRightElement
              mt="2px"
              w="fit-content"
              right="12px"
              bg="transparent"
              {...customMaxLengthProps}
            >
              <Flex
                gap="8px"
                alignItems="center"
                alignSelf="stretch"
                bg={hasError ? 'pastel-1-light' : 'transparent'}
                borderTopRightRadius="6px"
                borderBottomRightRadius="6px"
              >
                {rightIcon && (
                  <Icon
                    // @ts-expect-error - rightIcon is an IconType
                    as={rightIcon}
                    w="18px"
                    h="18px"
                    color={getElementColor}
                  />
                )}
                {!isDisabled && clear && customValue && (
                  <Icon
                    // @ts-expect-error - XClose is an IconType
                    as={XClose}
                    w="18px"
                    h="18px"
                    cursor="pointer"
                    onClick={handleClear}
                    color={getElementColor}
                  />
                )}
                {maxLength && (
                  <KText
                    variant="small_text"
                    textAlign="right"
                    whiteSpace="nowrap"
                    alignSelf="stretch"
                    display="flex"
                    alignItems="center"
                    color={getElementColor ?? 'secondary-700'}
                  >
                    {charCount}/{maxLength}
                  </KText>
                )}
              </Flex>
            </InputRightElement>
          )}
        </InputGroup>
        {additionalInfo && (
          <KText variant="paragraph" mt="6px">
            {additionalInfo}
          </KText>
        )}
        {error?.length > 0 && (
          <FormErrorMessage color="error-200">{error}</FormErrorMessage>
        )}
      </FormControl>
      {AfterInputGroupContent &&
        (inlineAfterContent ? (
          <Box flexShrink={0} whiteSpace="nowrap">
            <AfterInputGroupContent />
          </Box>
        ) : (
          <AfterInputGroupContent />
        ))}
    </>
  );
};
